import "./DataQualityShowcase.scss";
import React from "react";
import {ArrowBack, ArrowForward, ReplayOutlined} from "@mui/icons-material";
import {HERO, SCENARIOS} from "./showcaseData";
import {ScenarioStage} from "./components/ScenarioStage";
import {SummaryScreen} from "./components/SummaryScreen";

const SUMMARY_VIEW = SCENARIOS.length; // view index used for the summary screen
const LAST_SCENARIO = SCENARIOS.length - 1;

// Contextual label for the primary "advance" action, per step within a scenario.
const STEP_NEXT_LABEL = ['Ask the agents', 'Ask the audience', 'Reveal root cause'];
const STEP_LABELS = ['Question', 'Answers', 'Your turn', 'Root cause'];
const LAST_STEP = STEP_LABELS.length - 1;

/**
 * Self-contained, presentation-ready experience comparing the same procurement
 * agent answering the same question over unprepared vs. agent-ready data.
 * No backend — all content is rendered from ./showcaseData.
 */
export const DataQualityShowcasePage: React.FC = () => {
    // view: 0..N-1 = scenario, N = summary. step: 0..3 within a scenario.
    const [pos, setPos] = React.useState({view: 0, step: 0});
    const [replay, setReplay] = React.useState(0); // remount key for the stage

    const {view, step} = pos;
    const isSummary = view === SUMMARY_VIEW;

    // Remount the stage whenever the scenario changes so its reveal state resets.
    React.useEffect(() => {
        setReplay(r => r + 1);
    }, [pos.view]);

    const selectView = React.useCallback((v: number) => {
        setPos({view: v, step: 0});
    }, []);

    const goNext = React.useCallback(() => {
        setPos(({view, step}) => {
            if (view === SUMMARY_VIEW) return {view, step};
            if (step < LAST_STEP) return {view, step: step + 1};
            return {view: Math.min(view + 1, SUMMARY_VIEW), step: 0};
        });
    }, []);

    const goPrev = React.useCallback(() => {
        setPos(({view, step}) => {
            if (view === SUMMARY_VIEW) return {view: LAST_SCENARIO, step: LAST_STEP};
            if (step > 0) return {view, step: step - 1};
            if (view > 0) return {view: view - 1, step: LAST_STEP};
            return {view, step};
        });
    }, []);

    const reset = React.useCallback(() => {
        setPos(p => ({view: p.view, step: 0}));
        setReplay(r => r + 1);
    }, []);

    // Presenter keyboard navigation
    React.useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            // When a control button is focused, let it handle Space/Enter itself
            // (avoids a click + handler double-advance).
            if (e.key === ' ' && document.activeElement instanceof HTMLButtonElement) return;
            if (e.key === 'ArrowRight' || e.key === ' ' || e.key === 'PageDown') {
                e.preventDefault();
                goNext();
            } else if (e.key === 'ArrowLeft' || e.key === 'PageUp') {
                e.preventDefault();
                goPrev();
            } else if (e.key.toLowerCase() === 'r') {
                reset();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [goNext, goPrev, reset]);

    const scenario = !isSummary ? SCENARIOS[view] : null;
    const atVeryEnd = isSummary;
    const nextLabel = isSummary
        ? null
        : step < LAST_STEP
            ? STEP_NEXT_LABEL[step]
            : view === LAST_SCENARIO ? 'View summary' : 'Next scenario';

    return (
        <div className="dqs-root">
            <header className="dqs-hero">
                <span className="dqs-hero-eyebrow">{HERO.eyebrow}</span>
                <h1 className="dqs-hero-title">{HERO.title}</h1>
                <p className="dqs-hero-body">{HERO.body}</p>
            </header>

            <nav className="dqs-nav" aria-label="Scenarios">
                {SCENARIOS.map((s, i) => (
                    <button
                        key={s.id}
                        type="button"
                        className={`dqs-nav-item${view === i ? ' is-active' : ''}`}
                        onClick={() => selectView(i)}>
                        <span className="dqs-nav-index">{s.index}</span>
                        <span className="dqs-nav-label">{s.navLabel}</span>
                    </button>
                ))}
                <button
                    type="button"
                    className={`dqs-nav-item dqs-nav-summary${isSummary ? ' is-active' : ''}`}
                    onClick={() => selectView(SUMMARY_VIEW)}>
                    <span className="dqs-nav-index">✦</span>
                    <span className="dqs-nav-label">Summary</span>
                </button>
            </nav>

            <main className="dqs-main">
                {scenario
                    ? <ScenarioStage key={`${scenario.id}-${replay}`} scenario={scenario} step={step}/>
                    : <SummaryScreen/>}
            </main>

            <footer className="dqs-controls">
                <button type="button" className="dqs-ctrl dqs-ctrl-reset" onClick={reset} disabled={isSummary}>
                    <ReplayOutlined fontSize="small"/>
                    Reset
                </button>

                <div className="dqs-progress" aria-hidden={isSummary}>
                    {!isSummary && STEP_LABELS.map((label, i) => (
                        <span
                            key={i}
                            className={`dqs-progress-dot${i <= step ? ' is-on' : ''}${i === step ? ' is-current' : ''}`}
                            title={label}/>
                    ))}
                    <span className="dqs-progress-label">
                        {isSummary ? 'Summary' : STEP_LABELS[step]}
                    </span>
                </div>

                <div className="dqs-ctrl-group">
                    <button type="button" className="dqs-ctrl" onClick={goPrev}
                            disabled={view === 0 && step === 0}>
                        <ArrowBack fontSize="small"/>
                        Back
                    </button>
                    {atVeryEnd ? (
                        <button type="button" className="dqs-ctrl dqs-ctrl-primary" onClick={() => selectView(0)}>
                            <ReplayOutlined fontSize="small"/>
                            Restart
                        </button>
                    ) : (
                        <button type="button" className="dqs-ctrl dqs-ctrl-primary" onClick={goNext}>
                            {nextLabel}
                            <ArrowForward fontSize="small"/>
                        </button>
                    )}
                </div>
            </footer>
        </div>
    );
};
